import {
  ChoicesRelatedCombination,
  ChoicesRelatedCombinationsFilter,
} from "@generated"
import { getGraphQLClient } from "@lib/graphql-client"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useState } from "react"
import { getNormalizedRelatedCombinations } from "./helpers/get-normalized-related-combinations"

type Filter = ChoicesRelatedCombinationsFilter
const { sdk } = getGraphQLClient()

export function useRelatedCombinations(filter: Filter) {
  const [items, setItems] = useState<ChoicesRelatedCombination[]>([])
  const { hasNextPage, fetchNextPage, isFetchingNextPage, status } =
    useInfiniteQuery({
      queryKey: ["relatedCombinations", filter],
      queryFn: ({ pageParam }) =>
        sdk.RelatedCombinations({
          filter,
          pagination: {
            limit: 40,
            cursor: pageParam || undefined,
          },
        }),
      onSuccess(data) {
        // keeping the items in state makes sure we still have data when we're fetching the next page
        let items: ChoicesRelatedCombination[] = []
        for (const page of data.pages) {
          for (const edge of page?.choicesRelatedCombinations.items) {
            items.push(edge as ChoicesRelatedCombination)
          }
        }
        setItems(items)
      },
      getNextPageParam: (lastPage, pages) => {
        const { items } = lastPage.choicesRelatedCombinations
        const totalCurrentItems = pages.reduce(
          (acc, page) => acc + page.choicesRelatedCombinations.items.length,
          0
        )
        const firstPage = pages[0]
        if (totalCurrentItems >= firstPage.choicesRelatedCombinations.total) {
          return undefined
        }
        if (!items[items.length - 1]) {
          return undefined
        }
        return items[items.length - 1].combinations[0].id.toString()
      },
    })

  return {
    status,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    items: getNormalizedRelatedCombinations(items),
  }
}
